(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabPreviewLoading = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var DEFAULT_STEP_DURATION = 700;
  var DEFAULT_COMPLETE_DURATION = 300;
  // Marketing's threshold: preparation past this is slow enough to acknowledge,
  // but it is an informational notice - generation keeps running and completed
  // work is preserved. Never treat the notice as a failure.
  var DEFAULT_DELAY_NOTICE_MS = 20000;
  var DEFAULT_DELAY_NOTICE_COPY =
    'This is taking longer than usual. We\u2019re still preparing your preview. Your photos and progress are safe\u2014there\u2019s no need to start over.';
  // The sequence is marketing-owned: shop.metafields.cuddleclones.pj_pdp_loading
  // .steps, shared with the PDP upload loader and surfaced on the modal as
  // data-loading-steps. There is no copy baked in here — an unset metafield
  // means no sequence, the same way the PDP card falls back to plain copy.

  // Wireframe PREVIEW_STEPS ramps the bar from 15% to 94% across the sequence
  // before `complete` takes it to 100. Derived from the list length rather than
  // a fixed table, so marketing can add, remove or reorder phrases freely.
  var FIRST_PERCENT = 15;
  var LAST_PERCENT = 94;

  function readSteps(modal) {
    var raw = modal && modal.dataset ? modal.dataset.loadingSteps : '';
    var parsed = null;

    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        parsed = null;
      }
    }

    var steps = Array.isArray(parsed)
      ? parsed.filter(function (step) {
          return typeof step === 'string' && step.trim() !== '';
        })
      : [];

    return steps;
  }

  // Dwell per phrase, also marketing-owned (pj_pdp_loading.step_duration_ms), so
  // both surfaces cycle at the same pace.
  function readStepDuration(modal) {
    var raw = modal && modal.dataset ? Number(modal.dataset.loadingStepMs) : NaN;
    return raw > 0 ? raw : 0;
  }

  function stepPercent(index, total) {
    if (total <= 1) return LAST_PERCENT;
    return Math.round(
      FIRST_PERCENT + ((LAST_PERCENT - FIRST_PERCENT) * index) / (total - 1)
    );
  }

  function buildSteps(modal) {
    var steps = readSteps(modal);
    return steps.map(function (copy, index) {
      return { percent: stepPercent(index, steps.length), copy: copy };
    });
  }

  function replaceCount(copy, petCount) {
    return copy.replace(/\{count\}/g, String(petCount));
  }

  function createPreviewLoadingController(doc, options) {
    var config = options || {};
    var activeRun = 0;
    var delay =
      config.delay ||
      function (duration) {
        return new Promise(function (resolve) {
          root.setTimeout(resolve, duration);
        });
      };
    var processPets =
      config.processPets ||
      function (context) {
        var processing = root.PjDesignLabProcessing;
        return processing && typeof processing.prepare === 'function'
          ? processing.prepare(context)
          : Promise.resolve();
      };

    function element(id) {
      return doc.getElementById(id);
    }

    function modalEl() {
      return element('pvModal');
    }

    function copy(modal, key, fallback) {
      return (modal.dataset && modal.dataset[key]) || fallback;
    }

    function setHidden(node, hidden) {
      if (node) node.hidden = hidden;
    }

    function setProgress(percent, description) {
      var progress = element('pvProgressTrack');
      var fill = element('pvProgressFill');
      var percentage = element('pvTaskPct');
      var status = element('pvTaskDesc');

      if (fill) fill.style.width = percent + '%';
      if (percentage) percentage.textContent = percent + '%';
      if (status) status.textContent = description;
      if (progress) progress.setAttribute('aria-valuenow', String(percent));
    }

    /**
     * Preparation owns the whole canvas: the previewer's chrome - checkerboard,
     * zoom controls - belongs to a preview that does not exist yet, and showing
     * it behind the card reads as the loader sitting on top of a half-built
     * previewer. Mirrors the wireframe's `is-preparing` state.
     */
    function setPreparing(isPreparing) {
      var modal = modalEl();
      if (!modal || !modal.classList) return;
      if (isPreparing) {
        modal.classList.add('is-preparing');
      } else {
        modal.classList.remove('is-preparing');
      }
    }

    function setLoadingVisibility(isLoading) {
      var modal = modalEl();
      if (!modal) return;

      setPreparing(isLoading);

      setHidden(modal.querySelector('[data-pv-loading-sidebar]'), !isLoading);
      setHidden(modal.querySelector('[data-pv-region="loading"]'), !isLoading);
      setHidden(modal.querySelector('[data-pv-region="controls"]'), isLoading);
      setHidden(modal.querySelector('[data-pv-region="loaded"]'), isLoading);
      setHidden(modal.querySelector('[data-pv-region="error"]'), true);
    }

    function setFailureVisibility() {
      var modal = modalEl();
      if (!modal) return;

      setPreparing(false);
      setHidden(modal.querySelector('[data-pv-loading-sidebar]'), true);
      setHidden(modal.querySelector('[data-pv-region="loading"]'), true);
      setHidden(modal.querySelector('[data-pv-region="controls"]'), false);
      setHidden(modal.querySelector('[data-pv-region="loaded"]'), true);
      // The canvas has nothing to show, so the failure panel takes its place
      // rather than leaving an empty stage (INT: async generated styles).
      setHidden(modal.querySelector('[data-pv-region="error"]'), false);
    }

    function preparingPetsEl() {
      var modal = modalEl();
      return modal ? modal.querySelector('[data-pv-preparing-pets]') : null;
    }

    /**
     * The photos being prepared, shown beside the spinner so the wait is
     * visibly about the shopper's own pets rather than an anonymous loader.
     *
     * Built as real nodes with `src` assigned as a property: these URLs come
     * from the uploader, and string-built markup would put them in an HTML
     * parsing context for no benefit.
     */
    function renderPreparingPets(context) {
      var node = preparingPetsEl();
      if (!node) return;

      var images = (context && context.petImages) || [];
      var usable = images.filter(function (url) {
        return typeof url === 'string' && url !== '';
      });

      node.textContent = '';
      if (!usable.length) {
        node.hidden = true;
        return;
      }

      usable.forEach(function (url, index) {
        var img = doc.createElement('img');
        img.src = url;
        img.alt = 'Pet ' + (index + 1);
        img.loading = 'eager';
        img.decoding = 'async';
        node.appendChild(img);
      });
      node.hidden = false;
    }

    function clearPreparingPets() {
      var node = preparingPetsEl();
      if (!node) return;
      node.textContent = '';
      node.hidden = true;
    }

    function delayNoticeEl() {
      var modal = modalEl();
      return modal ? modal.querySelector('[data-pv-delay-message]') : null;
    }

    function hideDelayNotice() {
      var node = delayNoticeEl();
      if (!node) return;
      node.hidden = true;
      node.textContent = '';
    }

    function showDelayNotice() {
      var modal = modalEl();
      var node = delayNoticeEl();
      if (!modal || !node) return;
      node.textContent = copy(modal, 'loadingDelay', DEFAULT_DELAY_NOTICE_COPY);
      node.hidden = false;
    }

    function readDelayNoticeMs(modal) {
      var raw = modal && modal.dataset ? Number(modal.dataset.loadingDelayMs) : NaN;
      return raw > 0 ? raw : DEFAULT_DELAY_NOTICE_MS;
    }

    function reset(context) {
      var modal = modalEl();
      if (!modal) return false;

      hideDelayNotice();
      renderPreparingPets(context);

      var petCount = Math.max(1, Number(context && context.petCount) || 1);
      var isMulti = petCount > 1;
      var taskLabel = element('pvTaskLabel');
      var spinnerLabel = element('pvSpinnerLabel');
      var taskCopy = isMulti
        ? copy(modal, 'loadingTaskMulti', "Customize Your Pet's Images")
        : copy(modal, 'loadingTaskSingle', "Customize Your Pet's Image");
      var spinnerCopy = isMulti
        ? copy(modal, 'loadingSpinnerMulti', 'Generating {count} pet portraits…')
        : copy(modal, 'loadingSpinnerSingle', "Generating your pet's portrait…");

      if (taskLabel) taskLabel.textContent = replaceCount(taskCopy, petCount);
      if (spinnerLabel) spinnerLabel.textContent = replaceCount(spinnerCopy, petCount);

      setLoadingVisibility(true);
      setProgress(0, copy(modal, 'loadingInitial', 'Preparing your preview…'));
      return true;
    }

    function emit(name, detail) {
      if (typeof doc.dispatchEvent !== 'function' || typeof root.CustomEvent !== 'function') return;
      doc.dispatchEvent(new root.CustomEvent(name, { detail: detail }));
    }

    /**
     * Real progress, when the store reports it: the bar follows units actually
     * completed rather than a timer, so it cannot show more progress than has
     * been made. Phrases keep cycling for copy; only the percentage changes
     * hands. Falls back to the stepped sequence when no progress is reported
     * (nothing eager to measure).
     */
    function listenForProgress(run) {
      if (!root.addEventListener || typeof root.CustomEvent !== 'function') {
        return function () {};
      }

      var handler = function (event) {
        if (run !== activeRun) return;
        var detail = (event && event.detail) || {};
        if (!detail.total) return;
        var percent = Math.round(
          FIRST_PERCENT + (LAST_PERCENT - FIRST_PERCENT) * (detail.ratio || 0)
        );
        var status = element('pvTaskDesc');
        setProgress(percent, status ? status.textContent : '');
      };

      root.addEventListener('pj-design-lab:preparation-progress', handler);
      return function () {
        root.removeEventListener('pj-design-lab:preparation-progress', handler);
      };
    }

    async function start(context) {
      var run = ++activeRun;
      var modal = modalEl();
      if (!reset(context) || !modal) return false;

      var processingPromise;
      try {
        processingPromise = Promise.resolve(processPets(context || {})).then(
          function (value) {
            return { ok: true, value: value };
          },
          function (error) {
            return { ok: false, error: error };
          }
        );
      } catch (error) {
        processingPromise = Promise.resolve({ ok: false, error: error });
      }

      // Armed for the whole run, not per phrase: the threshold is the total
      // preparation time, and the phrase sequence may finish long before the
      // work does.
      var delayTimer = root.setTimeout(function () {
        if (run === activeRun) showDelayNotice();
      }, config.delayNoticeMs || readDelayNoticeMs(modal));

      var stopProgress = listenForProgress(run);

      try {
        var steps = buildSteps(modal);
        var stepDuration =
          config.stepDuration || readStepDuration(modal) || DEFAULT_STEP_DURATION;

        for (var index = 0; index < steps.length; index += 1) {
          if (run !== activeRun) return false;

          var step = steps[index];
          setProgress(step.percent, step.copy);
          await delay(stepDuration);
        }

        var processingResult = await processingPromise;
        if (!processingResult.ok) throw processingResult.error;
        if (run !== activeRun) return false;

        setProgress(100, copy(modal, 'loadingComplete', 'Preview ready!'));
        await delay(config.completeDuration || DEFAULT_COMPLETE_DURATION);
        if (run !== activeRun) return false;

        hideDelayNotice();
        clearPreparingPets();
        setLoadingVisibility(false);
        emit('pj-design-lab:preview-loading-complete', {
          petCount: Math.max(1, Number(context && context.petCount) || 1),
        });
        return true;
      } catch (error) {
        if (run === activeRun) {
          hideDelayNotice();
          clearPreparingPets();
          setFailureVisibility();
          emit('pj-design-lab:preview-loading-failed', { error: error });
        }
        return false;
      } finally {
        root.clearTimeout(delayTimer);
        stopProgress();
      }
    }

    /**
     * Opens straight onto the finished Lab, skipping the phrase sequence: used
     * when everything the modal would prepare is already cached, so reopening
     * never charges the shopper a second wait for the same work.
     */
    function showReady() {
      var modal = modalEl();
      if (!modal) return false;

      activeRun += 1;
      hideDelayNotice();
      clearPreparingPets();
      setLoadingVisibility(false);
      emit('pj-design-lab:preview-loading-skipped', {});
      return true;
    }

    function cancel() {
      activeRun += 1;
    }

    return {
      start: start,
      cancel: cancel,
      reset: reset,
      showReady: showReady,
      setPreparing: setPreparing,
      showDelayNotice: showDelayNotice,
      hideDelayNotice: hideDelayNotice,
      renderPreparingPets: renderPreparingPets,
      clearPreparingPets: clearPreparingPets,
    };
  }

  var controller = root.document ? createPreviewLoadingController(root.document) : null;

  return {
    buildSteps: buildSteps,
    readStepDuration: readStepDuration,
    DEFAULT_DELAY_NOTICE_MS: DEFAULT_DELAY_NOTICE_MS,
    DEFAULT_DELAY_NOTICE_COPY: DEFAULT_DELAY_NOTICE_COPY,
    createPreviewLoadingController: createPreviewLoadingController,
    start: controller && controller.start,
    cancel: controller && controller.cancel,
    reset: controller && controller.reset,
  };
});
